import { Component, EventEmitter, Input, Output, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AccountRequestCloseModelBase, FileParameter, CloseAccountRequestsClient, AccountModel, SchwabClient, StandingInstruction } from 'src/app/core/clients/generated/client';
import { SnackbarService } from 'src/app/core/services/snackbar/snackbar.service';
import { fadeIn } from 'src/app/shared/constants/animations';
import { ConfirmationModalComponent } from 'src/app/shared/modals/confirmation-modal/confirmation-modal.component';
import { ConfirmationModalData } from 'src/app/shared/modals/confirmation-modal/modal-data.models';
import { AccountRequestModalHeaderComponent } from '../shared/components/account-request-modal-header/account-request-modal-header.component';
import { RequestExitConfirmationData } from '../shared/constants/workflow-navigation.constants';
import { DeliveryMethodEnum } from '../shared/enums/delivery-methods.enum';
import { AccountUpdateStepModel, AccountWorkflowNavModel, SolicitationOptionModel } from '../shared/models/workflow.models';
import { CloseAccountFormGroup } from './models/close-account-form.model';
import { AccountUpdateStepEnum, RequestTypeEnum } from '../shared/enums/account-update.enum';
import { TradeFrequencyEnum } from '../shared/enums/trade-frequency.enum';
import { DeliveryOptionsFormGroup } from '../shared/models/delivery-options-form.models';
import { debounceTime, distinctUntilChanged, Subscription } from 'rxjs';

@Component({
  selector: 'app-close-account-request',
  templateUrl: './close-account-request.component.html',
  animations: [fadeIn]
})
export class CloseAccountRequestComponent implements OnInit, OnDestroy {
  @Output() canceled = new EventEmitter();
  @Input() account?: AccountModel;
  @Input() workflow?: AccountWorkflowNavModel;
  @Input() solicitation?: SolicitationOptionModel;
  @ViewChild('header') header?: AccountRequestModalHeaderComponent;
  standingInstructions?: StandingInstruction[] = [];
  loadingPage = false;
  requestTypeEnum = RequestTypeEnum;
  deliveryMethodEnum = DeliveryMethodEnum;
  isSubmitting = false;
  isSubmitted = false;
  isComplete = false;
  submitCount = 0;
  crmRequestID?: number;
  accountUpdateStepEnum = AccountUpdateStepEnum;
  currentStep = this.accountUpdateStepEnum.step1;
  fedTaxClone: number | undefined;
  stateTaxClone: number | undefined;

  deliveryOptionsFormGroup = this.fb.group({
    deliveryMethod: [DeliveryMethodEnum.ach, [Validators.required]],
    standingInstruction: [null, [Validators.required]],
    tradeFrequency: [TradeFrequencyEnum.oneTime, [Validators.required]],
    initiationDate: [new Date(), [Validators.required]],
    files: null,
    isOvernightCheckDelivery: false,
    //journalCashRecipientAccountNumber: ['', [Validators.required]],
    //wireDeliveryOptionId: ['', [Validators.required]],
  }) as DeliveryOptionsFormGroup;

  closeAccountFormGroup = this.fb.group({
    reason: [null, [Validators.required]],
    billAccountOnClosing: true,
    stateTaxAmount: [null],
    federalTaxAmount: [null],
    isTaxWithholdingsChanged: this.fb.control(false),
    deliveryOptions: this.deliveryOptionsFormGroup
  }) as CloseAccountFormGroup;

  pages: AccountUpdateStepModel[] = [
    {
      formGroup: this.closeAccountFormGroup,
      value: this.accountUpdateStepEnum.step1,
    },
    {
      formGroup: this.closeAccountFormGroup,
      value: this.accountUpdateStepEnum.step2,
    },
  ];

  constructor(
    private fb: UntypedFormBuilder,
    private dialog: MatDialog,
    private schwabClient: SchwabClient,
    private dialogRef: MatDialogRef<CloseAccountRequestComponent>,
    private snackbarService: SnackbarService,
    private closeAccountRequestsClient: CloseAccountRequestsClient,
  ) { }

  private taxChangedSubscription?: Subscription;
  private stateTaxAmountSubscription?: Subscription;
  private federalTaxAmountSubscription?: Subscription;

  ngOnDestroy(): void {
    this.unsubscribeChangeDetection();
  }

  unsubscribeChangeDetection(): void {
    this.stateTaxAmountSubscription?.unsubscribe();
    this.federalTaxAmountSubscription?.unsubscribe();
    this.taxChangedSubscription?.unsubscribe();
  }

  ngOnInit(): void {

    this.closeAccountFormGroup?.controls.federalTaxAmount.patchValue(this.account?.federalTaxWithholding);
    this.closeAccountFormGroup?.controls.stateTaxAmount.patchValue(this.account?.stateTaxWithholding);
    this.fedTaxClone = this.account?.federalTaxWithholding;
    this.stateTaxClone= this.account?.stateTaxWithholding;

    //adding blank option for Other to display in drop down lists
    const blankInstructionOption : StandingInstruction = {
      bankAccountNumber: '',
      bankName: '',
      direction: '',
      transactionType: 'ALL',
      displayValue: 'Other (New Account)',
      hasIaAuthority: false,
    };

    if (this.account?.schwabMasterAccount && this.account?.schwabFormattedAccount) {
      this.loadingPage = true;
      this.schwabClient.getAccountStandingInstructions(this.account?.investmentAccountID, this.account?.schwabMasterAccount, this.account?.schwabFormattedAccount).subscribe({
        next: results => {
          const outgoing = results.filter(x => x.direction === 'OUTGOING');
          this.standingInstructions = outgoing;
          this.standingInstructions?.push(blankInstructionOption);
          this.loadingPage = false;
        },
        error: () => {
          this.loadingPage = false;
        },
        complete: () => {
          this.loadingPage = false;
        },
      });
    } else {
      this.standingInstructions?.push(blankInstructionOption);
    }

    //remove validators of control if no results so it's not required.
    // if (this.standingInstructions === undefined)
    //   this.closeAccountFormGroup.controls.deliveryOptions.controls.standingInstruction.setValidators(null);
    this.setupValueChangeDetection();
  }

  closeRequest(): void {
    if (this.isSubmitted && this.isComplete) {
      this.dialogRef.close(false);
      return;
    }

    const confirmationDialog = this.dialog.open<ConfirmationModalComponent, ConfirmationModalData>(ConfirmationModalComponent, {
      data: RequestExitConfirmationData
    });

    confirmationDialog.afterClosed().subscribe(res => {
      if (res) this.dialogRef.close(false);
      else return;
    });
  }

  cancelRequest(): void {
    const confirmationDialog = this.dialog.open<ConfirmationModalComponent, ConfirmationModalData>(ConfirmationModalComponent, {
      data: RequestExitConfirmationData
    });

    confirmationDialog.afterClosed().subscribe(res => {
      if (res) {
        this.canceled.emit();
      } else return;
    });
  }

  onSubmit(): void {
    if (this.account) {
      if (this.closeAccountFormGroup.invalid) {
        this.closeAccountFormGroup.markAllAsTouched();
      } else {
        this.isSubmitting = true;
        const postModel = this.constructPostModel(this.account);
        this.postRequest(postModel);
      }
    }
  }

  // DistinctUntilChanged won't work on amount inputs as the control may be updated manually or programmatically.
  // In case of the latter, emitChange event won't emit and won't be caught by change detection next time user happens to enter a previously matching value
  setupValueChangeDetection(): void {
    if (this.closeAccountFormGroup) {

      this.stateTaxAmountSubscription = this.closeAccountFormGroup?.controls.stateTaxAmount.valueChanges.pipe(distinctUntilChanged(), debounceTime(600)).subscribe({
        next: stateTaxAmount => {
          this.closeAccountFormGroup?.controls.stateTaxAmount.patchValue(stateTaxAmount);
          // if (!Number.isNaN(stateTaxAmount)) {
          //   this.recalculate();
          // }

          this.checkTaxChanged();
        }
      });

      this.federalTaxAmountSubscription = this.closeAccountFormGroup?.controls.federalTaxAmount.valueChanges.pipe(distinctUntilChanged(), debounceTime(1000)).subscribe({
        next: federalTaxAmount => {
          this.closeAccountFormGroup?.controls.federalTaxAmount.patchValue(federalTaxAmount);
          this.checkTaxChanged();
        }
      });


      this.taxChangedSubscription = this.closeAccountFormGroup?.controls.isTaxWithholdingsChanged?.valueChanges.pipe(distinctUntilChanged(), debounceTime(600)).subscribe({
        next: isChanged => {
          this.closeAccountFormGroup?.controls.isTaxWithholdingsChanged?.patchValue(isChanged);

          if (!isChanged){
            this.closeAccountFormGroup?.controls.federalTaxAmount.patchValue(this.account?.federalTaxWithholding);
            this.closeAccountFormGroup?.controls.stateTaxAmount.patchValue(this.account?.stateTaxWithholding);
          }
        }
      });
    }
  }

  checkTaxChanged(): void {
    let taxChanged = false;

    if (this.closeAccountFormGroup?.value.federalTaxAmount != this.fedTaxClone)
      taxChanged = true;

    if (this.closeAccountFormGroup?.value.stateTaxAmount != this.stateTaxClone)
      taxChanged = true;

    if (this.closeAccountFormGroup?.value.isTaxWithholdingsChanged != taxChanged)
      this.closeAccountFormGroup?.controls.isTaxWithholdingsChanged.patchValue(taxChanged);
  }

  constructPostModel(account: AccountModel): AccountRequestCloseModelBase {
    const standingInstructions = this.closeAccountFormGroup.value.deliveryOptions.standingInstruction;

    return {
      investmentAccountID: account.investmentAccountID,
      // 🔴 NOT SENT AT ALL, DELIBERATELY (AndyP, 2026-10-05). The picker no longer asks who
      // initiated the request, so there is no answer to file and we do not invent one. `false`
      // would assert the client asked for it and `true` that the advisor did -- both are
      // regulatory statements about a conversation nobody recorded, and the old default made one
      // of them on every request. The key is omitted, `AccountRequest*ModelBase` takes it as
      // `bool?`, and it reaches [req] as NULL -- `bit` with no NOT NULL -- reading as "not asked".
      //
      // ⚠️ `undefined`, NOT `null`, and not a cast. The API models were widened to `bool?`
      // under ACMTECH-1014, so the generated client now types this `boolean | undefined` and the
      // honest value type-checks on its own. A cast here would hide the next contract change.
      isSolicited: undefined,
      requestDetail: {
        notes: this.closeAccountFormGroup.value.reason,
        isCloseDateBilled: this.closeAccountFormGroup.value.billAccountOnClosing,
        deliveryMethodID: this.closeAccountFormGroup.value.deliveryOptions.deliveryMethod,
        isOvernightCheckDelivery: this.closeAccountFormGroup.value.deliveryOptions.isOvernightCheckDelivery ?? undefined,
        wireDeliveryOptionID: this.closeAccountFormGroup.value.deliveryOptions.wireDeliveryOptionId ?? undefined,
        deliveryInstruction: standingInstructions ? standingInstructions.displayValue as string : undefined,
        hasIaAuthority: standingInstructions ? standingInstructions.hasIaAuthority as boolean : undefined,
        fromAccountNumber: this.closeAccountFormGroup.controls.deliveryOptions.value.journalCashRecipientAccountNumber ?? undefined,
        stateTax: this.closeAccountFormGroup.value.stateTaxAmount ?? undefined,
        federalTax: this.closeAccountFormGroup.value.federalTaxAmount ?? undefined,
        changeTaxWithholdings: this.closeAccountFormGroup.value.isTaxWithholdingsChanged ?? undefined,
      }
    };
  }

  postRequest(closeRequest: AccountRequestCloseModelBase): void {
    const files: FileParameter[] = this.closeAccountFormGroup.controls.deliveryOptions.value.files?.map(file => {
      return { data: file, fileName: file.name };
    }) || [];

    this.closeAccountRequestsClient.createCloseAccountRequest(files, closeRequest).subscribe({
      next: (result) => {
        //used to pass to complete page for survey
        this.crmRequestID = result;
      },
      error: () => {
        this.submitCount++;
        // for work around on sometimes the first request failing submitting to the crm.
        if (this.submitCount < 2){
          console.log('received error, retrying in 2 seconds');
          setTimeout(() =>{
            this.onSubmit();
          }, 2000);
        } else {
          this.isSubmitting = false;
          this.isSubmitted = true;
          this.isComplete = false;
          this.submitCount = 0;
        }
      },
      complete: () => {
        this.isSubmitting = false;
        this.isSubmitted = true;
        this.isComplete = true;
      }
    });
  }

  previousStep(): void {
    if (this.currentStep > this.accountUpdateStepEnum.step1) {
      this.currentStep--;
    }
  }

  nextStep(formGroup: UntypedFormGroup): void {
    if (formGroup.invalid) {
      formGroup.markAllAsTouched();
    } else {
      if ((this.currentStep < this.accountUpdateStepEnum.step2)) {
        this.currentStep++;
      }
    }
  }
}